import type { Tournament } from '@brush/api-client';
import { describeApiError } from '@brush/api-client';
import { ErrorNotice, ErrorRetry, Spinner } from '@brush/ui';
import { Check, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { resultImportCandidates, usable } from '../../../import/candidates';
import { parseCsv } from '../../../import/csv';
import { resultColumnFor } from '../../../import/resultColumns';
import { buildResultsPlan, fileCardIds } from '../../../import/resultPlan';
import {
  useCardHolders,
  useImportResults,
  useRoomTemplates,
  type ResultsImportOutcome,
} from '../../../queries';
import { btnGhost, btnPrimary, Modal } from '../../Modal';
import { FileStep } from './FileStep';
import { ReviewResultsStep } from './ReviewResultsStep';
import { TemplateStep } from './TemplateStep';

type Step = 'template' | 'file' | 'review' | 'run';

const STEPS: { key: Step; label: string }[] = [
  { key: 'template', label: 'Template' },
  { key: 'file', label: 'File' },
  { key: 'review', label: 'Review' },
  { key: 'run', label: 'Import' },
];

/** See the note on the tournament importer — the same reasoning, and the same 5 MB. */
const MAX_BYTES = 5 * 1024 * 1024;

function StepBar({ current }: { current: Step }) {
  const at = STEPS.findIndex((s) => s.key === current);
  return (
    <ol className="mb-6 flex shrink-0 items-center gap-2 text-xs">
      {STEPS.map((step, index) => {
        const done = index < at;
        const here = index === at;
        return (
          <li key={step.key} className="flex items-center gap-2">
            <span
              className={`flex size-5 items-center justify-center rounded-full text-[10px] font-semibold ${
                here
                  ? 'bg-felt-700 text-white'
                  : done
                    ? 'bg-felt-50 text-felt-700'
                    : 'bg-ink-800/5 text-ink-800/40'
              }`}
            >
              {done ? <Check className="size-3" /> : index + 1}
            </span>
            <span className={here ? 'font-medium text-ink-950' : 'text-ink-800/50'}>
              {step.label}
            </span>
            {index < STEPS.length - 1 ? (
              <span className="mx-1 h-px w-4 bg-ink-800/15" aria-hidden />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Reading a room's own file of finishing positions into one tournament.
 *
 * The tournament importer's four steps, for the same reason: nothing is written until an operator
 * has seen what the file says, and the template decides how the file is read so it is chosen
 * first. What differs is the shape of the job — a results file describes one tournament, which is
 * the one this dialog was opened from rather than anything the file names.
 */
export function ImportResultsModal({
  roomId,
  tournament,
  onClose,
}: {
  roomId: string;
  tournament: Tournament;
  onClose: () => void;
}) {
  const templatesQ = useRoomTemplates(roomId);
  const importer = useImportResults(roomId);

  const [step, setStep] = useState<Step>('template');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [file, setFile] = useState<{ name: string; rows: string[][] } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [outcome, setOutcome] = useState<ResultsImportOutcome | null>(null);

  const candidates = useMemo(
    () => resultImportCandidates(templatesQ.data ?? []),
    [templatesQ.data],
  );
  const ready = usable(candidates);

  // One template is not a choice — but "one" means one candidate, not one that happens to be
  // usable today. See the tournament importer for why the distinction matters.
  const only = candidates.length === 1 && ready.length === 1 ? ready[0] : null;
  if (only && selectedId === null && step === 'template') {
    setSelectedId(only.entry.template.id);
    setStep('file');
  }

  const selected = candidates.find(
    (c) => c.entry.template.id === selectedId && c.fieldKeys !== null,
  );

  // The file's cards, and the room's players holding them — asked of the server in batches rather than
  // matched against the room's whole roster (performance rank 2). Asked as soon as a file is chosen.
  const cards = useMemo(
    () =>
      selected?.fieldKeys && file
        ? fileCardIds({ fields: selected.entry.template.fields, fieldKeys: selected.fieldKeys, rows: file.rows })
        : null,
    [selected, file],
  );
  const holdersQ = useCardHolders(roomId, cards);

  const plan = useMemo(() => {
    if (!selected?.fieldKeys || !file || !holdersQ.data) return null;
    return buildResultsPlan({
      roomId,
      tournament,
      fields: selected.entry.template.fields,
      fieldKeys: selected.fieldKeys,
      rows: file.rows,
      players: holdersQ.data,
      // V3: the marker this room's files put in the card column for an unclaimed place.
      unclaimedMarker: selected.entry.content?.unclaimedPlaceMarker ?? null,
    });
  }, [selected, file, roomId, tournament, holdersQ.data]);

  const takeFile = (chosen: File) => {
    setFileError(null);
    setFile(null);

    if (!/\.csv$/i.test(chosen.name)) {
      setFileError(`“${chosen.name}” isn't a CSV file. Only CSV can be imported.`);
      return;
    }
    if (chosen.size > MAX_BYTES) {
      setFileError(
        `That file is ${(chosen.size / 1024 / 1024).toFixed(1)} MB. The importer takes files up to 5 MB.`,
      );
      return;
    }

    void chosen
      .text()
      .then((text) => {
        const rows = parseCsv(text);
        if (rows.length === 0) {
          setFileError('That file is empty.');
          return;
        }
        setFile({ name: chosen.name, rows });
      })
      .catch(() => setFileError("That file couldn't be read."));
  };

  const start = () => {
    if (!plan || plan.results.length === 0) return;
    setProgress(0);
    setOutcome(null);
    setStep('run');
    importer.mutate(
      {
        tournamentId: tournament.id,
        newPlayers: plan.newPlayers,
        // Built here rather than in the plan because the ids do not exist until the players are
        // created. Everything else about each row was decided at review time.
        build: (idsByCard) =>
          plan.results.map((r) =>
            // An unclaimed place is sent as one, explicitly: no player, never looked up by the card
            // its file gave it, and that card carried as provenance (ADR 0013).
            r.unclaimed !== null
              ? {
                  playerId: null,
                  position: r.place,
                  prize: r.prize,
                  prizeNotes: r.prizeNotes,
                  isInDeal: r.isInDeal,
                  buyins: r.buyins,
                  unclaimed: true,
                  sourceCardId: r.sourceCardId,
                  id: null,
                }
              : {
                  playerId: r.player?.id ?? idsByCard.get(r.cardId.toLowerCase()) ?? '',
                  position: r.place,
                  prize: r.prize,
                  prizeNotes: r.prizeNotes,
                  isInDeal: r.isInDeal,
                  buyins: r.buyins,
                  unclaimed: false,
                  sourceCardId: null,
                  id: null,
                },
          ),
        onProgress: setProgress,
      },
      { onSuccess: setOutcome },
    );
  };

  // ---- body ----

  let body = null;
  let footer = null;
  let shown: Step = step;

  if (templatesQ.isLoading) {
    body = <Spinner label="Loading templates…" />;
  } else if (templatesQ.isError) {
    body = (
      <ErrorRetry message="Couldn't load templates." onRetry={() => void templatesQ.refetch()} />
    );
  } else if (candidates.length === 0) {
    body = (
      <p className="py-8 text-center text-sm text-ink-800/60">
        No template available to this room is set up for importing tournament results. Templates
        are defined by the platform, and can be limited to rooms running particular features.
      </p>
    );
    footer = (
      <button type="button" onClick={onClose} className={btnGhost}>
        Close
      </button>
    );
  } else if (step === 'run') {
    const total = (plan?.newPlayers.length ?? 0) + 1;
    body = (
      <RunResults
        total={total}
        done={progress}
        outcome={outcome}
        error={describeApiError(importer.error)}
      />
    );
    footer = importer.isPending ? null : (
      <button type="button" onClick={onClose} className={btnPrimary}>
        Done
      </button>
    );
  } else if (step === 'review' && plan) {
    body = <ReviewResultsStep plan={plan} />;
    footer = (
      <>
        <button type="button" onClick={() => setStep('file')} className={btnGhost}>
          Back
        </button>
        <button
          type="button"
          onClick={start}
          disabled={plan.errorCount > 0 || plan.results.length === 0}
          className={btnPrimary}
        >
          {plan.results.length === 0
            ? 'Nothing to import'
            : `Record ${plan.results.length} ${plan.results.length === 1 ? 'place' : 'places'}`}
        </button>
      </>
    );
  } else if (step === 'review' && holdersQ.isError) {
    body = (
      <ErrorRetry
        message={describeApiError(holdersQ.error) ?? "Couldn't look up the file's cards."}
        onRetry={() => void holdersQ.refetch()}
      />
    );
    footer = (
      <button type="button" onClick={() => setStep('file')} className={btnGhost}>
        Back
      </button>
    );
  } else if (step === 'review' && !plan) {
    body = <Spinner label="Looking up the file's cards…" />;
  } else if (step === 'file' && selected?.fieldKeys) {
    body = (
      <FileStep
        templateName={selected.entry.template.name}
        fields={selected.entry.template.fields}
        fieldKeys={selected.fieldKeys}
        file={file ? { name: file.name, lines: file.rows.length } : null}
        error={fileError}
        onFile={takeFile}
        onClearFile={() => {
          setFile(null);
          setFileError(null);
        }}
        onChangeTemplate={only ? null : () => setStep('template')}
        describeColumn={resultColumnFor}
      />
    );
    footer = (
      <>
        <button
          type="button"
          onClick={() => (only ? onClose() : setStep('template'))}
          className={btnGhost}
        >
          {only ? 'Cancel' : 'Back'}
        </button>
        <button
          type="button"
          onClick={() => setStep('review')}
          disabled={!file}
          className={btnPrimary}
        >
          Validate
        </button>
      </>
    );
  } else {
    shown = 'template';
    body = (
      <TemplateStep candidates={candidates} selectedId={selectedId} onSelect={setSelectedId} />
    );
    footer = (
      <>
        <button type="button" onClick={onClose} className={btnGhost}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => setStep('file')}
          disabled={!selected}
          className={btnPrimary}
        >
          Next
        </button>
      </>
    );
  }

  const subtitle = [tournament.name, selected?.entry.template.name, file?.name]
    .filter(Boolean)
    .join(' · ');

  const fills = shown === 'review';

  return (
    <Modal
      title="Import results"
      subtitle={subtitle}
      onClose={onClose}
      width="max-w-5xl"
      fill={fills}
      footer={footer}
    >
      {candidates.length > 0 ? <StepBar current={shown} /> : null}
      {fills ? <div className="flex min-h-0 flex-1 flex-col">{body}</div> : body}
    </Modal>
  );
}

/**
 * The run itself. Shorter than the tournament importer's because there is far less to say: the
 * results go in one request, so the only thing with a count is the players created ahead of them.
 */
function RunResults({
  total,
  done,
  outcome,
  error,
}: {
  total: number;
  done: number;
  outcome: ResultsImportOutcome | null;
  error: string | null;
}) {
  const pct = total === 0 ? 100 : Math.round((Math.min(done, total) / total) * 100);

  if (outcome === null) {
    return (
      <div className="py-8">
        <p className="mb-3 text-center text-sm text-ink-800/70">
          Recording results… {done} of {total}
        </p>
        <div className="h-2 overflow-hidden rounded-full bg-ink-800/10">
          <div className="h-full bg-felt-700 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <ErrorNotice message={error} className="mt-4" />
      </div>
    );
  }

  return (
    <div className="space-y-3 py-6">
      {outcome.failure === null ? (
        <p className="flex items-center justify-center gap-2 text-sm text-ink-950">
          <Check className="size-4 text-felt-700" />
          Recorded {outcome.resultsRecorded}{' '}
          {outcome.resultsRecorded === 1 ? 'place' : 'places'}.
        </p>
      ) : (
        <ErrorNotice message={outcome.failure} />
      )}

      {outcome.playersCreated > 0 ? (
        <p className="flex items-center justify-center gap-1.5 text-xs text-ink-800/70">
          <UserPlus className="size-3.5" />
          {outcome.playersCreated} new {outcome.playersCreated === 1 ? 'player' : 'players'} added
          to this room.
        </p>
      ) : null}
    </div>
  );
}
